import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { resolveImageUrl } from '@/lib/image';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BooksPage() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    age_range: 'all',
    genre: 'all',
    search: '',
    indian_only: false,
  });

  useEffect(() => {
    fetchBooks();
  }, [filters]);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.age_range && filters.age_range !== 'all') params.append('age_range', filters.age_range);
      if (filters.genre && filters.genre !== 'all') params.append('genre', filters.genre);
      if (filters.indian_only) params.append('is_indian_author', 'true');
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/books?${params.toString()}`);
      setBooks(response.data);
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setFilters({ ...filters, search: e.target.value });
  };

  return (
    <div className="page-transition py-12 px-6 md:px-12">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-primary mb-4" data-testid="books-page-title">
            Children's Books
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl" data-testid="books-page-description">
            Carefully selected books that nurture imagination, empathy, and curiosity.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="font-body text-sm font-medium mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title, author or illustrator..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  className="pl-10"
                  data-testid="books-search-input"
                />
              </div>
            </div>
            <div>
              <label className="font-body text-sm font-medium mb-2 block">Age Range</label>
              <Select value={filters.age_range || "all"} onValueChange={(value) => setFilters({ ...filters, age_range: value === "all" ? "" : value })}>
                <SelectTrigger data-testid="books-age-filter">
                  <SelectValue placeholder="All Ages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  <SelectItem value="0-6 months">0-6 months</SelectItem>
                  <SelectItem value="6 months-1 year">6 months-1 year</SelectItem>
                  <SelectItem value="1+ years">1+ years</SelectItem>
                  <SelectItem value="2+ years">2+ years</SelectItem>
                  <SelectItem value="3+ years">3+ years</SelectItem>
                  <SelectItem value="4+ years">4+ years</SelectItem>
                  <SelectItem value="0-5 years">0-5 years</SelectItem>
                  <SelectItem value="6+ years">6+ years</SelectItem>
                  <SelectItem value="9+ years">9+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-sm font-medium mb-2 block">Genre</label>
              <Select value={filters.genre || "all"} onValueChange={(value) => setFilters({ ...filters, genre: value === "all" ? "" : value })}>
                <SelectTrigger data-testid="books-genre-filter">
                  <SelectValue placeholder="All Genres" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genres</SelectItem>
                  <SelectItem value="Picture Book">Picture Book</SelectItem>
                  <SelectItem value="Board Book">Board Book</SelectItem>
                  <SelectItem value="Chapter Book">Chapter Book</SelectItem>
                  <SelectItem value="Fiction">Fiction</SelectItem>
                  <SelectItem value="Non-Fiction">Non-Fiction</SelectItem>
                  <SelectItem value="Potty Training">Potty Training</SelectItem>
                  <SelectItem value="Grief">Grief</SelectItem>
                  <SelectItem value="Science">Science</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border/60">
            <label className="flex items-center gap-2 cursor-pointer w-fit" htmlFor="books-indian-filter">
              <Checkbox
                id="books-indian-filter"
                checked={filters.indian_only}
                onCheckedChange={(checked) => setFilters({ ...filters, indian_only: !!checked })}
                data-testid="books-indian-filter"
              />
              <span className="font-body text-sm font-medium">Show Indian authors only</span>
            </label>
          </div>
        </div>

        {/* Books Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground" data-testid="books-loading">Loading books...</p>
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground" data-testid="books-empty-state">No books found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="books-grid">
            {books.map((book) => (
              <Link
                key={book.id}
                to={`/books/${book.id}`}
                className="group"
                data-testid={`book-card-${book.id}`}
              >
                <div className="bg-card border border-border/50 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden h-full">
                  <div className="relative h-64 overflow-hidden bg-muted">
                    {book.image_url ? (
                      <img
                        src={resolveImageUrl(book.image_url)}
                        alt={book.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="h-16 w-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 space-y-3">
                    <div>
                      <h3 className="font-heading text-2xl font-normal text-foreground group-hover:text-primary transition-colors">
                        {book.title}
                      </h3>
                      {book.author && (
                        <p className="text-sm text-muted-foreground mt-1">by {book.author}</p>
                      )}
                      {book.illustrator && (
                        <p className="text-xs text-muted-foreground mt-0.5">Illustrated by {book.illustrator}</p>
                      )}
                    </div>
                    {book.is_indian_author && (
                      <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mr-2">
                        Indian author
                      </span>
                    )}
                    {book.age_range && (
                      <span className="inline-block bg-accent/10 text-accent text-xs font-semibold px-3 py-1 rounded-full">
                        {book.age_range}
                      </span>
                    )}
                    {book.description && (
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                        {book.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}